import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Droplets, Plus, Search, X, Pencil, Trash2, SlidersHorizontal, Table2, Rows3, MapPin } from 'lucide-react';
import { Button, Toast } from '../../components/ui/FormField';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, GradientOverlayActions, ViewModeSwitch } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { wellApi, companyApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Well, Company, ListQuery } from '../../types/api';

type WellView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'wells.viewMode';
function loadWellView(): WellView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface WellCol {
  key: string;
  label: string;
  sortKey: string;
  width: string;
  show: boolean;
  mono?: boolean;
  primary?: boolean;
  render: (w: Well) => string;
}

export function WellsPage() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const recordActivity = useActivityStore((s) => s.record);
  const [searchParams] = useSearchParams();

  const siteIdParam = Number(searchParams.get('siteId') ?? 0);
  const siteNameParam = searchParams.get('siteName') ?? '';
  const [filterCompanyId, setFilterCompanyId] = useState(0);
  const [companies, setCompanies] = useState<Company[]>([]);

  const [viewMode, setViewModeRaw] = useState<WellView>(loadWellView);
  const setViewMode = (v: WellView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';

  const fetchFn = useCallback((q: ListQuery) => {
    if (siteIdParam > 0) return wellApi.getAllBySiteId(siteIdParam, q);
    if (isSuperAdmin) {
      return filterCompanyId > 0
        ? wellApi.getAllByCompanyId(filterCompanyId, q)
        : wellApi.getAll(q);
    }
    return wellApi.getAllByCompanyId(Number(authUser?.companyId ?? 0), q);
  }, [siteIdParam, isSuperAdmin, filterCompanyId, authUser?.companyId]);

  const crud = useCrudPage<Well>({ fetchFn, initialSortCol: 'ServiceDate', initialSortDir: 'Descending' });

  useEffect(() => {
    if (isSuperAdmin) {
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Columns — same set as the old UI; nothing is combined in compact (only the
  // actions column drops to a hover overlay). Hidden cols filtered out by role/site.
  const allCols: WellCol[] = [
    { key: 'company', label: 'Company Name', sortKey: 'CompanyName', width: 'minmax(150px,1.3fr)', show: isSuperAdmin && !siteIdParam, render: (w) => w.company?.companyName ?? '—' },
    { key: 'wellName', label: 'Well Name', sortKey: 'WellName', width: 'minmax(180px,1.8fr)', show: true, primary: true, render: (w) => w.wellName ?? '—' },
    { key: 'uwi', label: 'UWI', sortKey: 'WellId', width: 'minmax(140px,1.3fr)', show: true, mono: true, render: (w) => w.wellId ?? '—' },
    { key: 'mob', label: 'Mobilization Date', sortKey: 'ServiceDate', width: 'minmax(130px,1fr)', show: true, mono: true, render: (w) => formatDate(w.serviceDate) || '—' },
    { key: 'rel', label: 'Release Date', sortKey: 'ServiceReleaseDate', width: 'minmax(120px,1fr)', show: true, mono: true, render: (w) => formatDate(w.serviceReleaseDate) || '—' },
    { key: 'loc', label: 'Surface Location', sortKey: 'SurfaceLocation', width: 'minmax(170px,1.5fr)', show: isSuperAdmin, render: (w) => w.site?.siteName ?? '—' },
    { key: 'gti', label: 'GTI Job Number', sortKey: 'GTIJobNumber', width: 'minmax(120px,1fr)', show: isSuperAdmin, mono: true, render: (w) => w.son ?? '—' },
    { key: 'perigon', label: 'Perigon Tool S/N', sortKey: 'PerigonToolSerialNumber', width: 'minmax(130px,1fr)', show: isSuperAdmin, mono: true, render: (w) => w.tool?.serialNumber ?? '—' },
    { key: 'rig', label: 'Rig', sortKey: 'Rig', width: 'minmax(90px,0.8fr)', show: isSuperAdmin, render: (w) => w.rig ?? '—' },
    { key: 'bit', label: 'Bit Size', sortKey: 'BitSize', width: 'minmax(60px,0.5fr)', show: isSuperAdmin, render: (w) => w.bs ?? '—' },
  ];
  const cols = allCols.filter((c) => c.show);
  const gridMinWidth = isSuperAdmin ? 1480 : undefined;
  // Actions column only exists for SuperAdmin (matches main: non-admins get no Add/Edit/Delete).
  const colTemplate = cols.map((c) => c.width).join(' ') + (compact || !isSuperAdmin ? '' : ' 116px');

  const buildFormParams = useCallback(() => {
    const params = new URLSearchParams();
    if (siteIdParam > 0) { params.set('siteId', String(siteIdParam)); params.set('siteName', siteNameParam); }
    if (filterCompanyId > 0) params.set('companyId', String(filterCompanyId));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [siteIdParam, siteNameParam, filterCompanyId]);

  const openEditRoute = (id: number) => navigate(`/dashboard/wells/edit/${id}${buildFormParams()}`);

  const handleDelete = async (well: Well) => {
    if (!confirm(`Delete well "${well.wellName}"?`)) return;
    try {
      await wellApi.delete({ id: well.id });
      crud.showToast('Well deleted');
      recordActivity({ type: 'delete', entity: 'Wells', label: well.wellName?.trim() || `Well #${well.id}`, user: authUser?.username });
      crud.load();
    } catch {
      crud.showToast('Failed to delete well', 'error');
    }
  };

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Publish title + Add Well action (deps keep the form URL current).
  usePageHeader(
    'Wells',
    isSuperAdmin ? <Button onClick={() => navigate(`/dashboard/wells/new${buildFormParams()}`)}><Plus size={16} /> Add Well</Button> : null,
    [buildFormParams, isSuperAdmin],
  );

  const showCompanyFilter = isSuperAdmin && siteIdParam === 0;
  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Site deep-link banner */}
      {siteIdParam > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
            background: 'rgba(87,154,59,0.06)', border: '1px solid rgba(87,154,59,0.18)', borderRadius: 10,
          }}
        >
          <MapPin size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--color-text-mid)' }}>
            Filtered to Surface Location: <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{siteNameParam}</span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => navigate('/dashboard/wells')}
            style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--color-accent)', background: 'transparent', border: '1px solid var(--color-accent)', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
          >
            All wells
          </button>
        </div>
      )}

      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search wells…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button onClick={() => { crud.setSearchText(''); crud.setOffset(0); }} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          {showCompanyFilter && (
            <WellsFilterPopover
              companyFilter={filterCompanyId}
              companies={companies}
              onChangeCompany={(v) => { setFilterCompanyId(v); crud.setOffset(0); }}
              onClear={() => { setFilterCompanyId(0); crud.setOffset(0); }}
            />
          )}
          {showCompanyFilter && filterCompanyId > 0 && (
            <button onClick={() => { setFilterCompanyId(0); crud.setOffset(0); }} style={{ fontSize: 12, color: 'var(--color-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 5, fontFamily: 'var(--font-body)', fontWeight: 500 }}>
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[{ id: 'comfortable', tip: 'Comfortable view', Icon: Table2 }, { id: 'compact', tip: 'Compact view', Icon: Rows3 }]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading ? 'Loading…' : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows (horizontally scrollable — wide for SuperAdmin) */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              minWidth: gridMinWidth,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            {cols.map((c) => (
              <SortHeader key={c.key} label={c.label} col={c.sortKey} activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            ))}
            {!compact && isSuperAdmin && <div style={{ textAlign: 'right' }}>Actions</div>}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : crud.data.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Droplets size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>No wells found</div>
              <div style={{ fontSize: 12 }}>{isSuperAdmin ? 'Add a new well to get started.' : 'No wells to display.'}</div>
            </div>
          ) : (
            <div>
              {crud.data.map((w, i) => (
                <WellRow
                  key={w.id ?? i}
                  well={w}
                  cols={cols}
                  compact={compact}
                  canDelete={isSuperAdmin}
                  colTemplate={colTemplate}
                  minWidth={gridMinWidth}
                  isLast={i === crud.data.length - 1}
                  onOpen={() => w.id && openEditRoute(w.id)}
                  onDelete={() => handleDelete(w)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.data.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> wells
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function WellRow({
  well: w,
  cols,
  compact,
  canDelete,
  colTemplate,
  minWidth,
  isLast,
  onOpen,
  onDelete,
}: {
  well: Well;
  cols: WellCol[];
  compact: boolean;
  canDelete: boolean;
  colTemplate: string;
  minWidth?: number;
  isLast: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        minWidth,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {cols.map((c) => (
        <Cell
          key={c.key}
          style={{
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            ...(c.mono ? { fontFamily: 'var(--font-mono)', fontSize: 13 } : {}),
            ...(c.primary ? { fontWeight: 500 } : {}),
          }}
        >
          {c.render(w)}
        </Cell>
      ))}

      {!compact && canDelete && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <IconButton title="Edit" onClick={onOpen}><Pencil size={17} /></IconButton>
          <IconButton title="Delete" onClick={onDelete} hoverDanger><Trash2 size={17} /></IconButton>
        </div>
      )}

      {compact && canDelete && (
        <GradientOverlayActions visible={hover} bgColor={rowBg} onEdit={onOpen} onDelete={onDelete} />
      )}
    </div>
  );
}

// Filter popover — Company (SuperAdmin, server-side fetch filter).
function WellsFilterPopover({
  companyFilter,
  companies,
  onChangeCompany,
  onClear,
}: {
  companyFilter: number;
  companies: Company[];
  onChangeCompany: (v: number) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const POP_WIDTH = 320;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({ top: rect.bottom + 8, left: Math.max(12, Math.min(rect.left, window.innerWidth - POP_WIDTH - 12)) });
  }, []);

  useLayoutEffect(() => { if (open) updateCoords(); }, [open, updateCoords]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return; setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updateCoords);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updateCoords);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = companyFilter > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', fontSize: 13, fontWeight: 500,
          fontFamily: 'var(--font-body)', cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`, borderRadius: 7, transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--color-accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>1</span>
        )}
      </button>

      {open && coords && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: coords.top, left: coords.left, width: POP_WIDTH, background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,32,0.12)', zIndex: 100, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>Filter by</div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-dim)', borderRadius: 4 }}>
              <X size={14} />
            </button>
          </div>
          <div style={{ padding: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11.5, color: 'var(--color-text-mid)', fontFamily: 'var(--font-body)', fontWeight: 500 }}>Company</span>
              <select
                value={companyFilter || ''}
                onChange={(e) => onChangeCompany(Number(e.target.value) || 0)}
                style={{
                  width: '100%', height: 34, padding: '0 30px 0 11px', fontSize: 13, fontFamily: 'var(--font-body)',
                  color: companyFilter ? 'var(--color-text)' : 'var(--color-text-mid)',
                  background: `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
                  border: `1px solid ${companyFilter ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  borderRadius: 7, appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', outline: 'none', cursor: 'pointer', fontWeight: companyFilter ? 500 : 400,
                }}
              >
                <option value="">All companies</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--color-border)', background: 'var(--color-panel-alt)' }}>
            <button onClick={onClear} disabled={!isActive} style={{ fontSize: 12, color: !isActive ? 'var(--color-text-dim)' : 'var(--color-accent)', background: 'transparent', border: 'none', cursor: !isActive ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, padding: '4px 4px' }}>Clear filter</button>
            <button onClick={() => setOpen(false)} style={{ fontSize: 12.5, fontWeight: 500, background: 'var(--color-accent)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Apply</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
